import {
  Box,
  Button,
  Card,
  deepCopy,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  ValidationErrors,
} from "@compass/core";
import {
  CustomDataModelFieldSelectListItem,
  CustomDataModelFieldSelectListItemViewResource,
  DataModelEnum,
  Permission,
  PermissionAction,
} from "generated/openApi";
import produce from "immer";
import "moment-timezone";
import { useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useNavigate } from "react-router-dom";
import { enumToString, isAuthorized } from "services";
import ApiService from "services/apiService";
import { initDataModel } from "utils";
import { customDataModelFieldPageRoute } from "../CustomDataModelFieldPage";
import { SelectListItem } from "./SelectListItem";

export type CustomDataModelFieldSelectListOrderMethod = "alphabetical" | "displayOrder";

export const SelectListItems = (props: {
  rootPermission: Permission;
  id: number;
  selectList: CustomDataModelFieldSelectListItem[];
  setSelectList: (value: CustomDataModelFieldSelectListItem[]) => void;
  validationErrors: ValidationErrors | string[];
}) => {
  const ROOT_PERMISSION: Permission = Permission.SystemUtility;
  const navigate = useNavigate();
  const [viewResource, setViewResource] = useState<CustomDataModelFieldSelectListItemViewResource>({});
  const displayOrders = props.selectList?.filter((x) => x.displayOrder).map((x) => x.displayOrder!);
  const maxDisplayOrder = displayOrders?.length ? Math.max(...displayOrders) : undefined;
  const orderMethod: CustomDataModelFieldSelectListOrderMethod = maxDisplayOrder ? "displayOrder" : "alphabetical";

  useEffect(() => {
    ApiService.customDataModelFieldSelectListItems.getViewResource().then((response) => {
      setViewResource(response);
    });
  }, []);

  const setAlphabeticalOrder = () => {
    let orderedSelectList = deepCopy(props.selectList);
    orderedSelectList.forEach((x) => (x.displayOrder = undefined));
    orderedSelectList = orderedSelectList.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    props.setSelectList(orderedSelectList);
  };

  const setDisplayOrder = () => {
    let orderedSelectList = deepCopy(props.selectList);
    let displayOrder = 1;
    orderedSelectList.forEach((x) => (x.displayOrder = displayOrder++));
    orderedSelectList = orderedSelectList.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    props.setSelectList(orderedSelectList);
  };

  const setEditMode = () => navigate(`../${customDataModelFieldPageRoute.getPath({ id: props.id, crudMode: "edit" })}`);

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    setEditMode();
    let reorderedItems = deepCopy(props.selectList);
    const [movedItem] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, movedItem);
    reorderedItems.forEach((x, index) => (x.displayOrder = index + 1));

    props.setSelectList(reorderedItems);
  };

  return (
    <>
      <Card
        header={
          <Stack direction="row" justifyContent="space-between">
            <Stack direction="row" spacing="md" alignItems={"center"}>
              <Typography variant="inherit">Select List</Typography>
              <ToggleButtonGroup value={orderMethod} exclusive size="small">
                <ToggleButton
                  value={"alphabetical"}
                  onClick={() => {
                    setEditMode();
                    setAlphabeticalOrder();
                  }}
                >
                  Use Alphabetical Order
                </ToggleButton>
                <ToggleButton
                  value={"displayOrder"}
                  onClick={() => {
                    setEditMode();
                    setDisplayOrder();
                  }}
                >
                  Use Manual Order
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            <Stack direction="row" alignItems="center" spacing="sm">
              {isAuthorized(ROOT_PERMISSION, PermissionAction.Create) && (
                <Button
                  onClick={() =>
                    props.setSelectList(
                      produce(props.selectList, (draft) => {
                        setEditMode();
                        draft.unshift(
                          initDataModel<CustomDataModelFieldSelectListItem>({
                            displayOrder: maxDisplayOrder ? maxDisplayOrder + 1 : undefined,
                          })
                        );
                      })
                    )
                  }
                  variant="outlined"
                >
                  + New
                </Button>
              )}
            </Stack>
          </Stack>
        }
      >
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId={enumToString(DataModelEnum.CustomDataModelFieldSelectListItem)}>
            {(provided) => (
              <Stack spacing="sm" ref={provided.innerRef} {...provided.droppableProps}>
                {props.selectList.map((selectListItem, index) => (
                  <Draggable key={index} draggableId={index.toString()} index={index}>
                    {(provided) => (
                      <Box ref={provided.innerRef} {...provided.draggableProps}>
                        <SelectListItem
                          dragHandleProps={provided.dragHandleProps}
                          rootPermission={ROOT_PERMISSION}
                          selectListItem={selectListItem}
                          setSelectListItem={(value) =>
                            props.setSelectList(
                              produce(props.selectList, (draft) => {
                                draft[index] = value;
                              })
                            )
                          }
                          viewResource={viewResource || {}}
                          cancelCreate={() =>
                            props.setSelectList(
                              produce(props.selectList, (draft) => {
                                draft.splice(index, 1);
                              })
                            )
                          }
                          validationErrors={props.validationErrors[index] || []}
                          setEditMode={setEditMode}
                          orderMethod={orderMethod}
                        />
                      </Box>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Stack>
            )}
          </Droppable>
        </DragDropContext>
      </Card>

      {/* <ListContext.Provider value={{ loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }) }}>
        <Outlet />
      </ListContext.Provider> */}
    </>
  );
};
